import { Component, Input, Output, EventEmitter } from "@angular/core";

import { BehaviorSubject, asyncScheduler } from "rxjs";
import { throttleTime } from "rxjs/operators";

import { AutoSubOnDestroy, AutoSub } from "../../providers/auto-sub";

/**
 * Create a json input code editor, with schema correction.
 * Example usage:  <json-input [modelUri]="'motes://user-survey.json'" [(model)]="mote.feedbackSurvey" [example]='{  myProp: "Did you have a nice tour?" }'> 
 * 
 * Add new JOSN schema:
 *      1) Add model JSON schema in motes-sdk/dev-tools/schema/gen-schema under INPUT_FILES
 *      2) Compile the JSON schema with  'npm run gen-json-schema' and commit auto generated result
 *      3) Add the JSON schema URI in the code-editor.module (monaco.languages.json.jsonDefaults).
 */
@AutoSubOnDestroy()
@Component({
    selector: 'json-input',
    template: `
        <div class="editor-example" *ngIf="example">
            <b>Example: {{ exampleLabel }}</b>
            <pre>{{ example }}</pre>
        </div>

        <ngx-monaco-editor 
            #monacoEditor
            style="height:100%;"
            *ngIf="editorModel"
            class="code-editor" 
            (click)="$event.stopImmediatePropagation()" 
            [options]="{ language: 'json' }" 
            [model]="editorModel" [ngModel]="inputModel" (ngModelChange)="inputChangeSubject.next($event)">
        </ngx-monaco-editor>
    `
})
export class JsonInputComponent {
    @Input() throttleTime: number = 1500;

    @Input() set modelUri(uri: string) {
        this.editorModel = {
            value: '',
            language: 'json',
            uri: uri
        };
    }

    @Input() set model(model: any) {
        // If this model is already loaded, don't update editor, as this will reset position of cursor in editor
        if(this.inputModel && model && model.key == this.modelKey) return;

        if(!this.editorModel) console.error("Could not set model json, model uri not initialized yet (please set modelUri input first)");
        else this.editorModel.value = model ? JSON.stringify(model, null, 4) : '';
        // This is needed to update model on second load
        this.inputModel = this.editorModel.value;
    }

    public example: string = "";
    @Input('example') set setExample(example) {
        this.example = JSON.stringify(example, null, 4);
    }
    @Input() exampleLabel: string = '';

    @Output() modelChange = new EventEmitter<any>();

    public editorModel: { value: string, language: 'json', uri: string } = null;
    public inputModel: any;
    public inputChangeSubject: BehaviorSubject<string> = new BehaviorSubject<string>(null);
    private modelKey: string = "";

    constructor() {
    }

    ngOnInit() {
        // Listen for editor json changes
        AutoSub(this).reg['editorChangeSub'] = this.inputChangeSubject.pipe(throttleTime(this.throttleTime, asyncScheduler, { leading: true, trailing: true })).subscribe((jsonStr) => {
            this.updateModel(jsonStr);
        });
    }

    public updateModel(modelStr: string) {
        if(modelStr == '') return this.modelChange.emit('');

        try {
            let json = JSON.parse(modelStr);
            if(json == null) return;
            // Emit new parsed json value
            this.modelChange.emit(json);
        }
        catch(err) {
            // Invalid json, do nothing
        }
    }
}
